<template>
    <div
        class="fixed bottom-0 inset-x-0 px-4 pb-4 sm:inset-0 sm:flex sm:items-center sm:justify-center z-top whitespace-normal"
        v-show="active"
    >
        <transition
            enter-active-class="transition ease-out duration-200"
            enter-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition ease-in duration-200"
            leave-class="opacity-100"
            leave-to-class="opacity-0"
        >
            <div class="fixed inset-0 transition-opacity" v-if="active">
                <div class="absolute inset-0 bg-gray-900 opacity-75"></div>
            </div>
        </transition>
        <transition
            enter-active-class="transition ease-out duration-200"
            enter-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to-class="opacity-100 translate-y-0 sm:scale-100"
            leave-active-class="transition ease-in duration-200"
            leave-class="opacity-100 translate-y-0 sm:scale-100"
            leave-to-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        >
            <div
                class="bg-white rounded p-12 overflow-hidden shadow-md transform transition-all sm:max-w-2xl sm:w-full max-h-screen overflow-y-auto relative"
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-headline"
                v-if="active"
                v-click-away="close"
            >
                <!-- Close Button -->
                <button
                    type="button"
                    @click="close"
                    class="absolute top-4 active:top-4 right-4 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-full p-1 transition-colors"
                    aria-label="Close modal"
                >
                    <svg
                        class="h-6 w-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M6 18L18 6M6 6l12 12"
                        />
                    </svg>
                </button>

                <!-- Header Section -->
                <div class="text-center pb-4 mb-6">
                    <h3
                        class="text-2xl md:text-3xl font-bold text-gray-900"
                        id="modal-headline"
                    >
                        We're sorry to see you go! 😢
                    </h3>
                </div>

                <!-- Survey Content Section -->
                <div class="survey-content">
                    <!-- Survey Content -->
                    <div v-if="loading" class="text-sm text-center text-gray-400 py-4">
                        Loading survey questions...
                    </div>

                    <div
                        v-else-if="
                            survey &&
                            survey.survey_blueprint &&
                            survey.survey_blueprint.questions
                        "
                        class="space-y-8"
                    >
                        <div
                            v-for="question in survey.survey_blueprint
                                .questions"
                            :key="question.id"
                            class="text-left"
                        >
                            <label
                                class="block text-base font-semibold text-black mb-4"
                            >
                                {{ question.question }}
                                <span
                                    v-if="question.required"
                                    class="text-red-500"
                                    >*</span
                                >
                            </label>

                            <!-- Question with multiple answers (show as radio buttons) -->
                            <div
                                v-if="
                                    question.answers &&
                                    question.answers.length > 1
                                "
                                class="space-y-4"
                            >
                                <div
                                    v-for="answer in question.answers"
                                    :key="answer.id"
                                >
                                    <div class="flex items-start">
                                        <div class="relative flex items-center">
                                            <input
                                                :id="`question_${question.id}_answer_${answer.id}`"
                                                :name="`question_${question.id}`"
                                                type="radio"
                                                :value="answer.id"
                                                v-model="
                                                    surveyAnswers[question.id]
                                                "

                                            />
                                        </div>
                                        <label
                                            :for="`question_${question.id}_answer_${answer.id}`"
                                            class="ml-3 block text-sm text-gray-900 cursor-pointer"
                                        >
                                            {{ answer.answer }}
                                        </label>
                                    </div>

                                    <!-- Show textarea when freeform option is selected -->
                                    <div
                                        v-if="
                                            answer.has_freeform_response &&
                                            surveyAnswers[question.id] ===
                                                answer.id
                                        "
                                        class="mt-2 pl-7"
                                    >
                                        <textarea
                                            v-model="
                                                surveyAnswers[
                                                    `question_${question.id}_freeform`
                                                ]
                                            "
                                            placeholder="Please provide details..."
                                            class="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                            rows="3"
                                        ></textarea>
                                    </div>
                                </div>
                            </div>

                            <!-- Pure text area question (single freeform answer) -->
                            <textarea
                                v-else-if="
                                    question.answers &&
                                    question.answers.length === 1 &&
                                    question.answers[0].has_freeform_response
                                "
                                v-model="surveyAnswers[question.id]"
                                placeholder="Please share your feedback..."
                                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                rows="4"
                            ></textarea>
                        </div>
                    </div>
                </div>

                <!-- Action Buttons -->
                <div class="mt-2 pt-4 flex flex-col items-center space-y-6">
                    <button
                        type="button"
                        :disabled="!canSubmit"
                        :class="[
                            'inline-flex justify-center rounded-lg border border-transparent px-6 py-3 text-lg leading-6 font-semibold shadow-sm transition ease-in-out duration-150 sm:text-sm sm:leading-5',
                            canSubmit
                                ? 'bg-red-600 text-white hover:bg-red-500 '
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed',
                        ]"
                        @click="confirm"
                    >
                        {{
                            isSubmitting
                                ? "Submitting..."
                                : "Cancel Subscription"
                        }}
                    </button>
                    <button
                        type="button"
                        class="text-sm text-gray-500 no-underline hover:underline hover:text-gray-900 focus-visible:text-gray-900  transition ease-in-out duration-150"
                        @click="close"
                    >
                        Nevermind, I do not want to cancel my subscription
                    </button>
                </div>
            </div>
        </transition>
    </div>
</template>

<script lang="ts">
import SvgIcon from "../SvgIcon.vue";
import { getCancelSurvey, submitCancelSurvey } from "@/api/surveys";
import { useUserStore } from "@/stores/userStore";

export default {
    components: { SvgIcon },
    props: {
        active: {
            type: Boolean,
            default: false,
        },
        csrfToken: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            survey: null,
            loading: false,
            surveyAnswers: {},
            isSubmitting: false,
            focusableElements: [],
            firstFocusableElement: null,
            lastFocusableElement: null,
        };
    },
    computed: {
        canSubmit() {
            if (this.isSubmitting) {
                return false;
            }

            if (
                !this.survey ||
                !this.survey.survey_blueprint ||
                !this.survey.survey_blueprint.questions
            ) {
                return false;
            }

            // Check if all required questions have been answered
            return this.survey.survey_blueprint.questions.every((question) => {
                if (question.required) {
                    return (
                        this.surveyAnswers[question.id] !== undefined &&
                        this.surveyAnswers[question.id] !== null
                    );
                }
                return true; // Non-required questions don't need to be answered
            });
        },
    },
    watch: {
        active(newValue) {
            if (newValue && !this.survey) {
                this.fetchSurvey();
            }
            if (newValue) {
                this.$nextTick(() => {
                    this.setupFocusTrap();
                });
            } else {
                this.removeFocusTrap();
            }
        },
    },
    methods: {
        setupFocusTrap() {
            const modal = this.$el.querySelector('[role="dialog"]');
            if (!modal) return;

            // Get all focusable elements within the modal
            const focusableSelectors = [
                "button:not([disabled])",
                "input:not([disabled])",
                "textarea:not([disabled])",
                "select:not([disabled])",
                "a[href]",
                '[tabindex]:not([tabindex="-1"])',
            ];

            this.focusableElements = Array.from(
                modal.querySelectorAll(focusableSelectors.join(","))
            );

            if (this.focusableElements.length > 0) {
                this.firstFocusableElement = this.focusableElements[0];
                this.lastFocusableElement =
                    this.focusableElements[this.focusableElements.length - 1];

                // Focus the first element (close button)
                this.firstFocusableElement.focus();

                // Add event listener for tab key
                modal.addEventListener("keydown", this.handleFocusTrap);
            }
        },
        removeFocusTrap() {
            const modal = this.$el?.querySelector('[role="dialog"]');
            if (modal) {
                modal.removeEventListener("keydown", this.handleFocusTrap);
            }
        },
        handleFocusTrap(e) {
            // Handle Escape key
            if (e.key === "Escape") {
                e.preventDefault();
                this.close();
                return;
            }

            if (e.key !== "Tab") return;

            if (e.shiftKey) {
                // Shift + Tab: moving backwards
                if (document.activeElement === this.firstFocusableElement) {
                    e.preventDefault();
                    this.lastFocusableElement.focus();
                }
            } else {
                // Tab: moving forwards
                if (document.activeElement === this.lastFocusableElement) {
                    e.preventDefault();
                    this.firstFocusableElement.focus();
                }
            }
        },
        async fetchSurvey() {
            this.loading = true;
            try {
                this.survey = await getCancelSurvey(this.csrfToken);
            } catch (error) {
                console.error("Failed to fetch survey:", error);
            } finally {
                this.loading = false;
            }
        },
        close() {
            this.$emit("close");
        },
        async confirm() {
            if (!this.canSubmit) {
                return;
            }

            this.isSubmitting = true;

            try {
                const promises = [];

                // Submit responses for each answered question
                for (const [questionId, answerId] of Object.entries(
                    this.surveyAnswers
                )) {
                    // Skip freeform text fields (they're handled with their parent question)
                    if (questionId.includes("_freeform")) {
                        continue;
                    }

                    const question =
                        this.survey.survey_blueprint.questions.find(
                            (q) => q.id === parseInt(questionId)
                        );
                    if (!question) continue;

                    let freeformText = null;

                    // Check if this question has a corresponding freeform response
                    const freeformKey = `question_${questionId}_freeform`;
                    if (this.surveyAnswers[freeformKey]) {
                        freeformText = this.surveyAnswers[freeformKey];
                    }

                    // For pure textarea questions (single freeform answer)
                    if (
                        question.answers &&
                        question.answers.length === 1 &&
                        question.answers[0].has_freeform_response
                    ) {
                        freeformText = answerId; // In this case, answerId is actually the text
                        const actualAnswerId = question.answers[0].id;

                        const payload = {
                            survey_id: this.survey.id,
                            question_id: parseInt(questionId),
                            answer_id: actualAnswerId,
                            freeform_response_text: freeformText,
                        };

                        promises.push(
                            submitCancelSurvey(payload, this.csrfToken)
                        );
                    } else {
                        // Regular multiple choice questions
                        const payload = {
                            survey_id: this.survey.id,
                            question_id: parseInt(questionId),
                            answer_id: answerId,
                            freeform_response_text: freeformText,
                        };

                        promises.push(
                            submitCancelSurvey(payload, this.csrfToken)
                        );
                    }
                }

                await Promise.all(promises);

                // Emit success and the survey answers for any additional processing
                this.$emit("confirm", this.surveyAnswers, this.survey);
            } catch (error) {
                console.error("Failed to submit survey:", error);
                // You might want to show an error message to the user here
                // For now, we'll still emit confirm so the cancellation can proceed
                this.$emit("confirm", this.surveyAnswers, this.survey);
            } finally {
                this.isSubmitting = false;
            }
        },
    },
    unmounted() {
        this.removeFocusTrap();
    },
};
</script>

<style scoped></style>
